import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import WhitecapColors from "@WhiteCap/Utils/WhiteCapColor";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    chartSections = "chartSections",
}

export interface TapAndDrillSection {
    fields: {
        tabTitle: string;
        chartTitle: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.chartSections]: TapAndDrillSection[];
    };
}

type Props = OwnProps & WidgetProps;

export interface TapAndDrillStyles {
    chartContainer?: InjectableCss;
    chartTitle?: InjectableCss;
    tabContainer?: InjectableCss;
    tabButton?: InjectableCss;
}

export const tapAndDrillStyles: TapAndDrillStyles = {
    chartContainer: {
        css: css`
            border: 1px solid ${getColor("secondary")};
        `,
    },
    chartTitle: {
        css: css`
            font-size: 26px;
            font-weight: 500;
            color: ${getColor("text.main")};
            text-align: center;
            line-height: 1.1;
            margin-top: 25px;
            margin-bottom: 20px;
        `,
    },
    tabContainer: {
        css: css`
            text-align: center;
        `,
    },
    tabButton: {
        css: css`
            display: inline-block;
            border: 1px solid ${getColor("secondary")};
            color:${getColor("text.main")};
            cursor: pointer !important;
            font-size: 14px;
            font-weight: 500;
            padding: 14px 20px;
            transition: all 0.3s;
            vertical-align: middle;
            white-space: normal;
        `,
    },
};

const styles = tapAndDrillStyles;

const TapAndDrillSection = ({
    pageId,
    chartTitle,
    tabTitle,
}: {
    pageId: string;
    chartTitle: string;
    tabTitle: string;
}) => {
    const uniqueZoneName = tabTitle.split(" ").join("");
    return (
        <StyledWrapper {...styles.chartContainer}>
            {chartTitle && <StyledWrapper {...styles.chartTitle}>{chartTitle}</StyledWrapper>}
            <Zone contentId={pageId} zoneName={`Content-${uniqueZoneName}`} />
        </StyledWrapper>
    );
};

const TapAndDrillWidget: React.FC<Props> = ({ fields, id }: Props) => {
    const [selectedChart, setSelectedChart] = React.useState({
        tabTitle: "",
        chartTitle: "",
    });

    React.useEffect(() => {
        if (fields.chartSections.length !== 0) {
            setSelectedChart({
                tabTitle: fields.chartSections[0].fields.tabTitle,
                chartTitle: fields.chartSections[0].fields?.chartTitle ? fields.chartSections[0].fields.chartTitle : "",
            });
        }
    }, [fields.chartSections]);

    if (fields.chartSections.length === 0) {
        return null;
    }

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.tabContainer}>
                {fields.chartSections.map(chartSection => (
                    <StyledWrapper
                        style={{
                            background:
                                chartSection.fields.tabTitle === selectedChart.tabTitle
                                    ? `${WhitecapColors.white}`
                                    : `${WhitecapColors.gray1}`,
                        }}
                        key={chartSection.fields.tabTitle}
                        {...styles.tabButton}
                        onClick={() =>
                            setSelectedChart({
                                tabTitle: chartSection.fields.tabTitle,
                                chartTitle: chartSection.fields.chartTitle,
                            })
                        }
                    >
                        {chartSection.fields.tabTitle}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
            <TapAndDrillSection pageId={id} chartTitle={selectedChart.chartTitle} tabTitle={selectedChart.tabTitle} />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: TapAndDrillWidget,
    definition: {
        group: "Basic",
        displayName: "Tap & Drill Chart",
        fieldDefinitions: [
            {
                name: fields.chartSections,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.tabTitle;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "tabTitle",
                        displayName: "Tab title",
                        editorTemplate: "TextField",
                        isRequired: true,
                        defaultValue: "",
                        tooltip: "This label will be displayed in the button",
                    },
                    {
                        name: "chartTitle",
                        displayName: "Chart title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        tooltip: "This title will be displayed above the chart",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
